import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { useWorkspace } from '../workspace/WorkspaceContext'
import './companyBranding.css'

export type CompanyBrand = { profileKey: string; displayName: string; primaryColor: string; accentColor: string; darkColor: string; surfaceColor: string; logoUrl: string; markUrl: string; revision: number }
type Context = { brand?: CompanyBrand; setScope: (scope: { route: string; companyId: string } | null) => void }
const Branding = createContext<Context>({ setScope: () => {} })
const paletteKeys = ['primary', 'accent', 'dark', 'surface', 'soft']

export function CompanyBranding({ children }: { children: ReactNode }) {
  const { access } = useAuth(), workspace = useWorkspace(), location = useLocation()
  const [scope, setScope] = useState<{ route: string; companyId: string } | null>(null)
  const scopedRoute = /^\/central\/(transmissao|gravacoes|cliente-configuracoes|plano)/.test(location.pathname)
  const companyId = scope?.route === location.pathname ? scope.companyId : scopedRoute ? workspace.companyId : workspace.companies.length === 1 ? workspace.companies[0].id : ''
  const candidate = workspace.companies.find(company => company.id === companyId)?.branding
  const brand = access.isGeneralAdministrator ? undefined : candidate ?? undefined
  useEffect(() => {
    const root = document.documentElement
    if (brand) {
      root.dataset.companyBrand = brand.profileKey
      const colors = [brand.primaryColor, brand.accentColor, brand.darkColor, brand.surfaceColor, brand.surfaceColor]
      paletteKeys.forEach((key, index) => root.style.setProperty(`--brand-${key}`, colors[index]))
    }
    return () => { delete root.dataset.companyBrand; paletteKeys.forEach(key => root.style.removeProperty(`--brand-${key}`)) }
  }, [brand])
  return <Branding.Provider value={{ brand, setScope }}>{children}</Branding.Provider>
}

export function useCompanyBrandScope(companyId: string) {
  const { setScope } = useContext(Branding), location = useLocation()
  useEffect(() => { setScope({ route: location.pathname, companyId }); return () => setScope(null) }, [companyId, location.pathname, setScope])
}

export function BrandLogo({ className }: { className?: string; compact?: boolean }) {
  const { brand } = useContext(Branding)
  const [failed, setFailed] = useState(false)
  const src = brand ? brand.markUrl : '/logo.png'
  useEffect(() => setFailed(false), [src])
  return <img className={`${className ?? ''}${brand ? ' company-brand-logo' : ''}`} src={failed ? '/logo.png' : src} alt={brand?.displayName ?? 'Spectra Guard AI'} onError={() => { if (!failed) setFailed(true) }} />
}

export function BrandName() { return <>{useContext(Branding).brand?.displayName ?? 'Spectra Guard AI'}</> }

export function CompanyBrandShowcase({ clientCount }: { clientCount?: number }) {
  const { brand } = useContext(Branding)
  if (!brand) return null
  return <div className="company-brand-showcase"><BrandLogo compact />{clientCount!==undefined&&<span className="company-client-count" aria-label={`${clientCount} clientes`} title={`${clientCount} clientes`}>{clientCount}</span>}</div>
}
